import { gql } from "@apollo/client";
import { FETCH_POLICY_NETWORK_ONLY, PRODUCT_TYPE_IDS } from "../../helpers/constants";
import { logError } from "../../helpers/logging";
import { getCountryIdFromUrl } from "../../helpers/urls.helper";
import { calculateLocalCartProductsCost, calculateLocalCartShippingFee } from "../../helpers/localCart";
import { MEMBER_CHECKOUT_INFOS } from "./member";
import { RECOMMENDATION_EXPLANATION_STATUS_FRAGMENT } from "./recommendationExplanation";
import { PRODUCT_FRAGMENT } from "./product";
import { GET_WINE_PRICE_RANGES_FRAGMENT } from "./wine";

/**
 * QUERIES
 * */

export const GET_LOCAL_CART_ORDER_MINIMUM_PRODUCT_TYPE = gql`
  query LocalCartOrderMinimumProductType($productId: Int!) {
    productById(id: $productId) {
      id
      productType {
        id
        canCountTowardsOrderMinimum
      }
    }
  }
`;

// Public, display-only metadata for the guest's proposed subscription preferences.
// Keep it separate from the persisted local cart and its transfer payload.
export const GET_GUEST_SUBSCRIPTION_WINE = gql`
  query GuestSubscriptionWine($id: Int!) {
    productById(id: $id) {
      id
      wine {
        id
        wineType {
          id
          wineClass {
            id
            name
          }
        }
        winePriceRange {
          id
          isEnabledForWineQuantity
        }
      }
    }
  }
`;

export const GET_GUEST_SUBSCRIPTION_SETTINGS = gql`
  ${GET_WINE_PRICE_RANGES_FRAGMENT}
  query GuestSubscriptionSettings {
    allWinePriceRanges {
      ...WinePriceRangesFragment
    }
    minBottlesForFreeShipping
    standardShippingFee
  }
`;

/**
 *  MUTATIONS
 * */
export const RESET_SHOPPING_CART = gql`
  mutation ResetShoppingCart($input: ResetShoppingCartInput!) {
    resetShoppingCart(input: $input) {
      isSuccessful
      errors {
        field
        messages
      }
    }
  }
`;

export const ADD_SHOPPING_CART_ITEM = gql`
  mutation AddShoppingCartItem($input: AddShoppingCartItemInput!) {
    addShoppingCartItem(input: $input) {
      errors {
        field
        messages
      }
    }
  }
`;

export const ADD_SHOPPING_CART_ITEM_BULK = gql`
  mutation BulkAddShoppingCartItems($input: BulkAddShoppingCartItemsInput!) {
    bulkAddShoppingCartItems(input: $input) {
      shoppingCart {
        id
        total
        totalProductsCost
        totalShippingFee
        shouldWinesBeAddedToSubscription
        shoppingcartitemSet {
          id
          quantity
          extraInfo
          isRedeemedWithPoints
          product {
            id
            name
            sellingPrice
            coverPhotoSmall
            productType {
              id
              name
            }
          }
        }
      }
      successfulItemsCount
      failedItemsCount
      totalItemsCount
      errors {
        field
        messages
      }
    }
  }
`;

// Opt-in guest subscription hydration requires the backend's explicit per-row results.
// Keep the legacy operation unchanged for callers outside this feature.
export const PREPARE_GUEST_CART_TRANSFER = gql`
  mutation PrepareGuestCartTransfer($input: PrepareGuestCartTransferInput!) {
    prepareGuestCartTransfer(input: $input) {
      requestId
      expiresAt
      errors {
        field
        messages
        code
      }
    }
  }
`;

export const HYDRATE_GUEST_SUBSCRIPTION_CART = gql`
  mutation HydrateGuestSubscriptionCart($input: BulkAddShoppingCartItemsInput!) {
    bulkAddShoppingCartItems(input: $input) {
      destination
      itemResults {
        inputIndex
        isSuccessful
        destination
        errors {
          field
          messages
          code
        }
      }
      successfulItemsCount
      failedItemsCount
      totalItemsCount
      errors {
        field
        messages
        code
      }
    }
  }
`;

export const DELETE_SHOPPING_CART_ITEM = gql`
  mutation DeleteShoppingCartItem($input: DeleteShoppingCartItemInput!) {
    deleteShoppingCartItem(input: $input) {
      isDeleted
      errors {
        field
        messages
      }
    }
  }
`;

export const DELETE_PRODUCT_TYPE_SHOPPING_CART_ITEMS = gql`
  mutation DeleteProductTypeShoppingCartItems($input: DeleteProductTypeShoppingCartItemsInput!) {
    deleteProductTypeShoppingCartItems(input: $input) {
      isDeleted
      errors {
        field
        messages
      }
    }
  }
`;

export const UPDATE_SHOPPING_CART_ITEM = gql`
  mutation UpdateShoppingCartItem($input: UpdateShoppingCartItemInput!) {
    updateShoppingCartItem(input: $input) {
      shoppingCart {
        id
        total
        totalShippingFee
        discount
        bottlesTowardsOrderMinimum
        shoppingcartitemSet {
          id
          quantity
          product {
            id
            sellingPrice
          }
          extraInfo
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const CREATE_ORDERS_PENDING_AUTHORIZATION = gql`
  mutation CreateOrdersPendingAuthorization($input: CreateOrdersPendingAuthorizationInput!) {
    createOrdersPendingAuthorization(input: $input) {
      clientSecret
      netTotal
      errors {
        field
        code
        messages
      }
      latestOrderId
    }
  }
`;

export const APPLY_REFERRAL_CODE = gql`
  mutation ApplyReferralCode($input: ApplyReferralCodeInput!) {
    applyReferralCode(input: $input) {
      isSuccessful
      discountApplied
      discountLimit
      errors {
        field
        messages
      }
    }
  }
`;

export const UPDATE_REFERRAL_CODE = gql`
  ${MEMBER_CHECKOUT_INFOS}
  mutation UpdateReferralCode($input: UpdateReferralCodeInput!) {
    updateReferralCode(input: $input) {
      me {
        ...MemberCheckoutInfos
      }
      errors {
        field
        messages
      }
    }
  }
`;

export const CHECKOUT = gql`
  mutation Checkout($input: CheckoutInput!) {
    checkout(input: $input) {
      isSuccessful
      latestOrderId
      errors {
        field
        messages
      }
    }
  }
`;

export const TOGGLE_SHOPPING_CART_SUBSCRIPTION = gql`
  mutation ToggleShoppingCartSubscription($input: ToggleShoppingCartSubscriptionInput!) {
    toggleShoppingCartSubscription(input: $input) {
      shouldWinesBeAddedToSubscription
      errors {
        field
        messages
      }
    }
  }
`;

export const GENERATE_ONE_OFF_RECOMMENDATIONS = gql`
  ${RECOMMENDATION_EXPLANATION_STATUS_FRAGMENT}
  mutation GenerateOneOffRecommendations($input: GenerateOneOffRecommendationsInput!) {
    generateOneOffRecommendations(input: $input) {
      isSuccessful
      newShoppingCartItem {
        id
        quantity
        extraInfo
        isRedeemedWithPoints
        product {
          id
          name
          slug
          sellingPrice
          pointCost
          coverPhotoSmall
          coverPhotoSmallPng
          backgroundColor
          productType {
            id
            name
          }
          productPhotos {
            id
            photoLarge
          }
          wine {
            id
            year
            country {
              id
              name
            }
            wineType {
              id
              name
              wineClass {
                id
                name
              }
            }
            wineRegion {
              id
              name
            }
            winePriceRange {
              id
            }
          }
        }
        recommendationExplanationStatus {
          ...RecommendationExplanationStatus
        }
      }
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Resolvers, Defaults & Type definitions
 * https://www.apollographql.com/docs/link/links/state
 * */

export const GET_LOCAL_SHOPPING_CART = gql`
  query LocalShoppingCart {
    localShoppingCart @client {
      countryId
      visible
      totalShippingFee
      totalProductsCost
      items {
        quantity
        product {
          id
          name
          sellingPrice
          coverPhotoSmall
          slug
          productType {
            id
            canCountTowardsOrderMinimum
          }
        }
        extraInfo {
          giftDeliveryId
          redBottles
          whiteBottles
          sparklingBottles
          roseBottles
          monthsPurchased
          bottlesPerBox
          spendPerBox
          isGift
        }
      }
    }
  }
`;

export const ADD_ITEM_TO_LOCAL_SHOPPING_CART = gql`
  mutation AddItemToLocalShoppingCart($input: AddItemToLocalShoppingCartInput!) {
    addItemToLocalShoppingCart(input: $input) @client {
      localShoppingCart {
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const ADD_ITEMS_TO_LOCAL_SHOPPING_CART = gql`
  mutation AddItemsToLocalShoppingCart($input: AddItemsToLocalShoppingCartInput!) {
    addItemsToLocalShoppingCart(input: $input) @client {
      localShoppingCart {
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const DELETE_ITEM_IN_LOCAL_SHOPPING_CART = gql`
  mutation DeleteItemInLocalShoppingCart($input: DeleteItemInLocalShoppingCartInput!) {
    deleteItemInLocalShoppingCart(input: $input) @client {
      localShoppingCart {
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const DELETE_ITEMS_BY_PRODUCT_TYPE_IN_LOCAL_SHOPPING_CART = gql`
  mutation DeleteItemsByProductTypeInLocalShoppingCart(
    $input: DeleteItemsByProductTypeInLocalShoppingCartInput!
  ) {
    deleteItemsByProductTypeInLocalShoppingCart(input: $input) @client {
      localShoppingCart {
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const INCREASE_QUANTITY_FOR_ITEM_IN_LOCAL_SHOPPING_CART = gql`
  mutation IncreaseQuantityItemInLocalShoppingCart($input: IncreaseQuantityItemInLocalShoppingCartInput!) {
    increaseQuantityForItemInLocalShoppingCart(input: $input) @client {
      localShoppingCart {
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const DECREASE_QUANTITY_FOR_ITEM_IN_LOCAL_SHOPPING_CART = gql`
  mutation DecreaseQuantityItemInLocalShoppingCart($input: DecreaseQuantityItemInLocalShoppingCartInput!) {
    decreaseQuantityForItemInLocalShoppingCart(input: $input) @client {
      localShoppingCart {
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const HIDE_LOCAL_SHOPPING_CART = gql`
  mutation HideLocalShoppingCart {
    hideLocalShoppingCart @client {
      localShoppingCart {
        visible
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const SHOW_LOCAL_SHOPPING_CART = gql`
  mutation ShowLocalShoppingCart {
    showLocalShoppingCart @client {
      localShoppingCart {
        visible
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const RESET_LOCAL_SHOPPING_CART = gql`
  mutation ResetLocalShoppingCart {
    resetLocalShoppingCart @client {
      localShoppingCart {
        visible
        items {
          product {
            id
            name
          }
        }
      }
    }
  }
`;

export const ONE_CLICK_TOP_UP = gql`
  mutation OneClickTopup($input: OneClickTopupInput!) {
    oneClickTopup(input: $input) {
      isSuccessful
      latestOrderId
      errors {
        field
        messages
      }
    }
  }
`;

export const CREATE_ONE_CLICK_TOP_UP_PAYMENT_INTENT = gql`
  mutation CreateOneClickTopupPaymentIntent($input: CreateOneClickTopupPaymentIntentInput!) {
    createOneClickTopupPaymentIntent(input: $input) {
      clientSecret
      errors {
        field
        messages
      }
      defaultPaymentMethodApiUuid
    }
  }
`;

export const REMOVE_SHOPPING_CART_DISCOUNT = gql`
  mutation RemoveShoppingCartDiscount($input: RemoveShoppingCartDiscountInput!) {
    removeShoppingCartDiscount(input: $input) {
      isDeleted
      errors {
        field
        messages
      }
      me {
        shoppingCart {
          discount
          discountCode
          discountType {
            id
            name
          }
        }
      }
    }
  }
`;

function findCartItem(items, productId) {
  return items.find((item) => item.product.id === productId);
}

function increaseQuantityInCartItem(item) {
  if (item) {
    return { ...item, quantity: item.quantity + 1 };
  }

  return undefined;
}

function decreaseQuantityInCartItem(item) {
  if (item && item.quantity > 0) {
    return { ...item, quantity: item.quantity - 1 };
  }

  return undefined;
}

function deleteItemFromCart(productId, items) {
  return items.filter((item) => item.product.id !== productId);
}

function deleteItemsByProductTypeFromCart(productTypeId, items) {
  return items.filter((item) => item.product.productType.id !== productTypeId);
}

const urlCountryId = getCountryIdFromUrl();

const defaultLocalShoppingCart = {
  visible: false,
  totalProductsCost: 0,
  totalShippingFee: 0,
  items: [],
  countryId: urlCountryId,
  __typename: "LocalShoppingCart",
};

/**
 * Pads a local cart product type to the full `ProductTypeInLocalShoppingCart` shape. Every writer must
 * produce a complete object: Apollo treats a cached object missing any selected field as making the whole
 * local cart unreadable. Wine always counts towards the order minimum. Other product types carry the flag
 * from PRODUCT_FRAGMENT or use a conservative fallback until refreshLocalCartOrderMinimumFlags fetches
 * current metadata during cache restoration.
 */
export function normaliseLocalProductType(productType) {
  const { id = null, canCountTowardsOrderMinimum } = productType || {};
  return {
    ...productType,
    id,
    canCountTowardsOrderMinimum:
      canCountTowardsOrderMinimum ?? id === PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_WINE,
    __typename: "ProductTypeInLocalShoppingCart",
  };
}

const LOCAL_PRODUCT_TYPE_CACHE_KEY_PREFIX = "ProductTypeInLocalShoppingCart:";

/**
 * Pads product types in a restored (persisted) local cart that predate the `canCountTowardsOrderMinimum`
 * field, so a guest's pre-deploy cart stays readable after a deploy. Local product types are normalised
 * by `__typename:id`, so this is a flat scan of the extracted cache. First step of
 * migratePersistedLocalCart, which owns the "runs BEFORE the @client defaults pass" contract.
 * @param {import("@apollo/client").InMemoryCache} cache
 * @returns {boolean} whether anything was migrated
 */
function migrateLocalProductTypes(cache) {
  const data = cache.extract();
  let hasChanged = false;
  Object.entries(data).forEach(([key, entry]) => {
    if (!key.startsWith(LOCAL_PRODUCT_TYPE_CACHE_KEY_PREFIX) || !entry) return;
    if ("canCountTowardsOrderMinimum" in entry) return;
    data[key] = normaliseLocalProductType(entry);
    hasChanged = true;
  });
  if (hasChanged) {
    cache.restore(data);
  }
  return hasChanged;
}

/**
 * Refresh configurable product types after restoring the cart. Always fetch current metadata, including
 * when an earlier migration already stored a false fallback. Wine and the gift/pack/reward/subscription
 * types have fixed roles in the minimum calculation and do not need a metadata request.
 * Failed lookups leave the cart intact and are retried on the next restoration.
 */
export async function refreshLocalCartOrderMinimumFlags(cache, client) {
  const items = cache.readQuery({ query: GET_LOCAL_SHOPPING_CART })?.localShoppingCart?.items ?? [];
  const fixedProductTypes = [
    PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_WINE,
    PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_GIFT,
    PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SPECIAL_PACK,
    PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_FREEBIE,
    PRODUCT_TYPE_IDS.DB_ID_PRODUCT_TYPE_SUBSCRIPTION,
  ];
  const productsByType = new Map();
  items.forEach((item) => {
    const product = item?.product;
    const typeId = product?.productType?.id;
    if (product?.id && typeId && !fixedProductTypes.includes(typeId)) {
      productsByType.set(typeId, product.id);
    }
  });

  await Promise.all(
    Array.from(productsByType, async ([typeId, productId]) => {
      try {
        const { data } = await client.query({
          query: GET_LOCAL_CART_ORDER_MINIMUM_PRODUCT_TYPE,
          variables: { productId },
          fetchPolicy: FETCH_POLICY_NETWORK_ONLY,
          errorPolicy: "none",
        });
        const productType = data?.productById?.productType;
        if (productType?.id !== typeId || typeof productType.canCountTowardsOrderMinimum !== "boolean") {
          return;
        }
        cache.modify({
          id: `${LOCAL_PRODUCT_TYPE_CACHE_KEY_PREFIX}${typeId}`,
          fields: {
            canCountTowardsOrderMinimum: () => productType.canCountTowardsOrderMinimum,
          },
        });
      } catch (error) {
        logError(error);
      }
    })
  );
}

/**
 * Every field of the `extraInfo` selection in GET_LOCAL_SHOPPING_CART. A
 * written object missing any selection-set field makes the whole query
 * unreadable to Apollo — one partial write and the next cart read returns
 * null, i.e. the guest cart appears empty. Writers only pass the fields they
 * know about, so the rest are padded to null here.
 */
const LOCAL_EXTRA_INFO_DEFAULTS = {
  giftDeliveryId: null,
  redBottles: null,
  whiteBottles: null,
  sparklingBottles: null,
  roseBottles: null,
  monthsPurchased: null,
  bottlesPerBox: null,
  spendPerBox: null,
  isGift: null,
};

/**
 * When saving a shopping cart item in the local state we need to provide __typename fields to let
 * apollo-client combine data.
 */
function normaliseLocalCartItem(shoppingCartItem) {
  if (!Number.isFinite(shoppingCartItem.product.sellingPrice) || shoppingCartItem.product.sellingPrice < 0) {
    throw new Error("This item's price is unavailable. Please refresh and try again.");
  }

  // A present extraInfo object must include every queried field, even when its value is null.
  const extraInfo = shoppingCartItem.extraInfo
    ? {
        ...LOCAL_EXTRA_INFO_DEFAULTS,
        ...shoppingCartItem.extraInfo,
        giftDeliveryId: shoppingCartItem.extraInfo.giftDeliveryId ?? null,
        redBottles: shoppingCartItem.extraInfo.redBottles ?? null,
        whiteBottles: shoppingCartItem.extraInfo.whiteBottles ?? null,
        sparklingBottles: shoppingCartItem.extraInfo.sparklingBottles ?? null,
        roseBottles: shoppingCartItem.extraInfo.roseBottles ?? null,
        monthsPurchased: shoppingCartItem.extraInfo.monthsPurchased ?? null,
        bottlesPerBox: shoppingCartItem.extraInfo.bottlesPerBox ?? null,
        spendPerBox: shoppingCartItem.extraInfo.spendPerBox ?? null,
        __typename: "ExtraInfoInLocalShoppingCart",
      }
    : null;

  return {
    quantity: shoppingCartItem.quantity,
    product: {
      ...shoppingCartItem.product,
      coverPhotoSmall:
        shoppingCartItem.product.coverPhotoSmall ?? shoppingCartItem.product.coverPhotoLarge ?? null,
      slug: shoppingCartItem.product.slug ?? "",
      productType: normaliseLocalProductType(shoppingCartItem.product.productType),
      __typename: "ProductInLocalShoppingCart",
    },
    extraInfo,
    __typename: "CartItemInLocalShoppingCart",
  };
}

function readLocalCartForAdd(cache, incomingItems) {
  const localShoppingCart =
    cache.readQuery({ query: GET_LOCAL_SHOPPING_CART, returnPartialData: true })?.localShoppingCart ??
    defaultLocalShoppingCart;

  return {
    ...defaultLocalShoppingCart,
    ...localShoppingCart,
    items: (localShoppingCart.items || []).map((item) => {
      if (Number.isFinite(item.product.sellingPrice) && item.product.sellingPrice >= 0) {
        return normaliseLocalCartItem(item);
      }

      // Older incomplete additions may already be persisted. Recover their price from
      // the item being added or the full catalog record before calculating cart totals.
      const product =
        findCartItem(incomingItems, item.product.id)?.product ||
        cache.readFragment({
          id: cache.identify({ __typename: "ProductObject", id: item.product.id }),
          fragment: PRODUCT_FRAGMENT,
          returnPartialData: true,
        });
      return normaliseLocalCartItem({ ...item, product: { ...item.product, ...product } });
    }),
  };
}

export const resolverLocalCart = {
  defaults: {
    localShoppingCart: defaultLocalShoppingCart,
  },
  resolvers: {
    Mutation: {
      addItemToLocalShoppingCart: async (obj, args, { cache }) => {
        const {
          input: { item, minBottlesForFreeShipping, standardShippingFee },
        } = args;
        const incomingItem = normaliseLocalCartItem(item);
        const localShoppingCart = readLocalCartForAdd(cache, [incomingItem]);
        const { items } = localShoppingCart;

        // If the wine is already in the cart, bump its quantity in place;
        // otherwise append it. The previous version computed the bumped item
        // but then discarded it (`newItems = [...items]`), so re-adding a wine
        // already in the cart silently did nothing (DEV-4465).
        // An add that carries extraInfo also applies it to the consolidated
        // item — the shopper's latest choice wins (e.g. flipping the advent
        // gift toggle between adds). An add without extraInfo keeps what's
        // there, so a plain re-add can't wipe gift metadata.
        const existingItem = findCartItem(items, item.product.id);
        const newItems = existingItem
          ? items.map((cartItem) =>
              cartItem.product.id === item.product.id
                ? {
                    ...cartItem,
                    quantity: cartItem.quantity + item.quantity,
                    extraInfo: item.extraInfo
                      ? {
                          ...LOCAL_EXTRA_INFO_DEFAULTS,
                          ...cartItem.extraInfo,
                          ...item.extraInfo,
                          __typename: "ExtraInfoInLocalShoppingCart",
                        }
                      : cartItem.extraInfo,
                  }
                : cartItem
            )
          : [...items, incomingItem];

        const newTotalCost = calculateLocalCartProductsCost(newItems);
        const newShippingFee = calculateLocalCartShippingFee(
          newItems,
          minBottlesForFreeShipping,
          standardShippingFee
        );
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            visible: true,
            items: newItems,
            totalProductsCost: newTotalCost,
            totalShippingFee: newShippingFee,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      addItemsToLocalShoppingCart: async (obj, args, { cache }) => {
        let {
          input: { items },
        } = args;
        items = items.map((item) => normaliseLocalCartItem(item));
        const localShoppingCart = readLocalCartForAdd(cache, items);
        const { items: localShoppingCartItems } = localShoppingCart;
        items = items.map((item) => {
          const cartItem = findCartItem(localShoppingCartItems, item.product.id);
          if (cartItem) {
            return { ...cartItem, quantity: cartItem.quantity + item.quantity };
          }
          return item;
        });
        items = [...items, ...localShoppingCartItems.filter((item) => !findCartItem(items, item.product.id))];
        items = items.map((item) => normaliseLocalCartItem(item));
        const newTotalCost = calculateLocalCartProductsCost(items);
        const newShippingFee = calculateLocalCartShippingFee(
          items,
          args.input.minBottlesForFreeShipping,
          args.input.standardShippingFee
        );
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            visible: true,
            items,
            totalProductsCost: newTotalCost,
            totalShippingFee: newShippingFee,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      deleteItemInLocalShoppingCart: async (obj, args, { cache }) => {
        const {
          localShoppingCart,
          localShoppingCart: { items },
        } = cache.readQuery({ query: GET_LOCAL_SHOPPING_CART });
        const newItems = deleteItemFromCart(args.input.productId, items);
        const newTotalCost = calculateLocalCartProductsCost(newItems);
        const newShippingFee = calculateLocalCartShippingFee(
          newItems,
          args.input.minBottlesForFreeShipping,
          args.input.standardShippingFee
        );
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            items: newItems,
            totalProductsCost: newTotalCost,
            totalShippingFee: newShippingFee,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      deleteItemsByProductTypeInLocalShoppingCart: async (obj, args, { cache }) => {
        const {
          localShoppingCart,
          localShoppingCart: { items },
        } = cache.readQuery({ query: GET_LOCAL_SHOPPING_CART });
        const newItems = deleteItemsByProductTypeFromCart(args.input.productTypeId, items);
        const newTotalCost = calculateLocalCartProductsCost(newItems);
        const newShippingFee = calculateLocalCartShippingFee(
          newItems,
          args.input.minBottlesForFreeShipping,
          args.input.standardShippingFee
        );
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            items: newItems,
            totalProductsCost: newTotalCost,
            totalShippingFee: newShippingFee,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      increaseQuantityForItemInLocalShoppingCart: async (obj, args, { cache }) => {
        const {
          localShoppingCart,
          localShoppingCart: { items },
        } = cache.readQuery({ query: GET_LOCAL_SHOPPING_CART });

        const increasedItem = increaseQuantityInCartItem(findCartItem(items, args.input.productId));
        const newItems = [...items.filter((item) => item.product.id !== args.input.productId), increasedItem];
        const newTotalCost = calculateLocalCartProductsCost(newItems);
        const newShippingFee = calculateLocalCartShippingFee(
          newItems,
          args.input.minBottlesForFreeShipping,
          args.input.standardShippingFee
        );

        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            items: newItems,
            totalProductsCost: newTotalCost,
            totalShippingFee: newShippingFee,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      decreaseQuantityForItemInLocalShoppingCart: async (obj, args, { cache }) => {
        const {
          localShoppingCart,
          localShoppingCart: { items },
        } = cache.readQuery({ query: GET_LOCAL_SHOPPING_CART });
        const { minBottlesForFreeShipping, standardShippingFee } = args.input;
        const item = findCartItem(items, args.input.productId);
        const shouldRemoveItem = item.quantity === 1;
        let newItems = items;
        if (shouldRemoveItem) {
          newItems = deleteItemFromCart(args.input.productId, items);
        } else {
          newItems = [
            ...items.filter((localItem) => localItem.product.id !== args.input.productId),
            decreaseQuantityInCartItem(item),
          ];
        }
        const newTotalCost = calculateLocalCartProductsCost(newItems);
        const newShippingFee = calculateLocalCartShippingFee(
          newItems,
          minBottlesForFreeShipping,
          standardShippingFee
        );
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            items: newItems,
            totalProductsCost: newTotalCost,
            totalShippingFee: newShippingFee,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      hideLocalShoppingCart: async (obj, args, { cache }) => {
        const { localShoppingCart } = cache.readQuery({
          query: GET_LOCAL_SHOPPING_CART,
        });
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            visible: false,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      showLocalShoppingCart: async (obj, args, { cache }) => {
        const { localShoppingCart } = cache.readQuery({
          query: GET_LOCAL_SHOPPING_CART,
        });
        const data = {
          __typename: "Store",
          localShoppingCart: {
            ...localShoppingCart,
            visible: true,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
      resetLocalShoppingCart: async (obj, args, { cache }) => {
        const data = {
          __typename: "Store",
          localShoppingCart: {
            visible: false,
            items: [],
            totalProductsCost: 0,
            totalShippingFee: 0,
            countryId: urlCountryId,
            __typename: "LocalShoppingCart",
          },
        };
        await cache.writeQuery({
          query: GET_LOCAL_SHOPPING_CART,
          data,
        });
        return data;
      },
    },
  },
};

/**
 * Salvages a guest cart persisted before the extraInfo selection gained a
 * field. The restored objects lack the new field, which makes
 * GET_LOCAL_SHOPPING_CART unreadable — and the fill-if-absent defaults pass
 * that runs at startup would then treat the whole cart as missing and reset
 * it, silently emptying every saved item (guest gift vouchers and welcome
 * packs always carry a non-null extraInfo). Reads what the cache can still
 * serve, pads each item's extraInfo up to the current shape, and writes the
 * cart back complete. A blob missing more than extraInfo fields stays
 * unreadable after the rewrite and the defaults pass still resets it — this
 * migration only widens what survives, never what breaks.
 * @returns {boolean} whether the cart was rewritten
 */
const migrateLocalCartExtraInfo = (cache) => {
  const { complete, result } = cache.diff({
    query: GET_LOCAL_SHOPPING_CART,
    optimistic: false,
    returnPartialData: true,
  });
  if (complete) return false;
  const cart = result?.localShoppingCart;
  if (!cart?.items?.length) return false;
  cache.writeQuery({
    query: GET_LOCAL_SHOPPING_CART,
    data: {
      localShoppingCart: {
        ...resolverLocalCart.defaults.localShoppingCart,
        ...cart,
        items: cart.items.filter((item) => item?.product).map(normaliseLocalCartItem),
      },
    },
  });
  return true;
};

/**
 * Migrates a restored (persisted) guest cart up to the current local schema so it stays readable: pads
 * product types that predate `canCountTowardsOrderMinimum`, then pads each item's extraInfo. Must run
 * BEFORE the @client defaults pass, which would otherwise replace an unreadable cart with the empty
 * default — unlike the other entries, resetting the cart destroys a guest's saved items.
 * @param {import("@apollo/client").InMemoryCache} cache
 * @returns {boolean} whether anything was migrated
 */
export const migratePersistedLocalCart = (cache) => {
  const migratedProductTypes = migrateLocalProductTypes(cache);
  const migratedExtraInfo = migrateLocalCartExtraInfo(cache);
  return migratedProductTypes || migratedExtraInfo;
};
